import {
  confirmAdmissionRecalculation,
  previewAdmissionRecalculation,
  type AdmissionRecalculationPreview,
  downloadMemberEntryReceipt,
  getMemberAdmissionPayments,
  recordMemberAdmissionPayment,
  type MemberAdmissionItem,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalModal,
  InfoPanel,
  SelectField,
  StatePanel,
  StatusBadge,
  canonicalStates,
  formatDateTime,
} from '@urbanik/ui';
import { FileCheck2, Plus, ReceiptText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './member-admission-payments.css';
import {
  MemberAdmissionBulkImport,
  RemainingAdmissionBulkVerification,
} from './MemberAdmissionBulkImport';
export function MemberAdmissionPaymentsPanel({
  memberId,
  mode = 'payments',
}: {
  memberId: string;
  mode?: 'payments' | 'documents';
}) {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading'),
    [items, setItems] = useState<MemberAdmissionItem[]>([]),
    [open, setOpen] = useState(false),
    [recalculation, setRecalculation] = useState<AdmissionRecalculationPreview | null>(null),
    [recalculationBusy, setRecalculationBusy] = useState(false),
    [recalculationMessage, setRecalculationMessage] = useState('');
  const openRecalculation = async (assignmentId: string) => {
    setRecalculationBusy(true);
    setRecalculationMessage('');
    try {
      setRecalculation(await previewAdmissionRecalculation(apiBase, memberId, assignmentId));
    } catch (error) {
      setRecalculationMessage(
        error instanceof Error ? error.message : 'No pudimos preparar el recálculo.',
      );
    } finally {
      setRecalculationBusy(false);
    }
  };
  const load = () => {
    setState('loading');
    getMemberAdmissionPayments(apiBase, memberId)
      .then((r) => {
        setItems(r.items);
        setState('success');
      })
      .catch(() => setState('error'));
  };
  useEffect(load, [memberId]);
  if (state !== 'success')
    return <StatePanel state={canonicalStates.find((x) => x.kind === state)!} />;
  const payments = items.flatMap((i) =>
    i.payments.map((p) => ({
      ...p,
      number: i.number,
      joinedOn: i.admission?.joinedOn ?? null,
      firstChargePeriod: i.admission?.firstChargePeriod ?? null,
    })),
  );
  const admissions = items.filter((i) => i.admission);
  const download = async (p: (typeof payments)[number]) => {
    const blob = await downloadMemberEntryReceipt(apiBase, memberId, p.id);
    const url = URL.createObjectURL(blob),
      a = document.createElement('a');
    a.href = url;
    a.download = p.receiptFileName;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="member-entry-payments" role="tabpanel">
      <header>
        <div>
          <span>{mode === 'payments' ? 'PAGOS DE INGRESO' : 'RESPALDOS DE INGRESO'}</span>
          <h2>{mode === 'payments' ? 'Alta histórica y pagos' : 'Comprobantes de ingreso'}</h2>
        </div>
        {mode === 'payments' ? (
          <div className="member-entry-payments__actions">
            <MemberAdmissionBulkImport onDone={load} />
            <RemainingAdmissionBulkVerification onDone={load} />
            <Button variant="primary" icon={<Plus />} onClick={() => setOpen(true)}>
              Registrar ingreso y pago
            </Button>
          </div>
        ) : null}
      </header>
      {admissions.length ? (
        <div className="member-entry-payments__list">
          {admissions.map((i) => (
            <article key={i.assignmentId}>
              <div className="member-entry-payments__icon">
                <ReceiptText />
              </div>
              <div>
                <strong>
                  Alícuota {i.number} · ingreso {i.admission!.joinedOn}
                </strong>
                <span>Primer periodo cobrable {i.admission!.firstChargePeriod.slice(0, 7)}</span>
                <small>{i.admission!.reason}</small>
              </div>
              <StatusBadge tone={i.admission!.dateStatus === 'verified' ? 'success' : 'warning'}>
                {i.admission!.dateStatus === 'verified' ? 'Fecha verificada' : 'Fecha provisional'}
              </StatusBadge>
              {i.admission!.recalculationStatus === 'pending' ? (
                <>
                  <StatusBadge tone="danger">Recalcular</StatusBadge>
                  <Button
                    disabled={recalculationBusy}
                    onClick={() => void openRecalculation(i.assignmentId)}
                  >
                    {recalculationBusy ? 'Calculando…' : 'Recalcular obligaciones'}
                  </Button>
                </>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
      {payments.length ? (
        <div className="member-entry-payments__list">
          {payments.map((p) => (
            <article key={p.id}>
              <div className="member-entry-payments__icon">
                {mode === 'documents' ? <FileCheck2 /> : <ReceiptText />}
              </div>
              <div>
                <strong>
                  Alícuota {p.number} · USD {Number(p.amount).toFixed(2)}
                </strong>
                <span>
                  {p.reference} · {p.receiptFileName}
                </span>
                <small>
                  Ingreso {p.joinedOn ?? 'pendiente'} · primera cuota{' '}
                  {p.firstChargePeriod?.slice(0, 7) ?? 'pendiente'} · pagado {p.paidOn}
                </small>
              </div>
              <Button onClick={() => void download(p)}>Descargar PDF</Button>
              <StatusBadge tone={p.status === 'approved' ? 'success' : 'warning'}>
                {p.status === 'pending_review' ? 'Pendiente de revisión' : p.status}
              </StatusBadge>
            </article>
          ))}
        </div>
      ) : (
        <InfoPanel title="Sin pagos de ingreso registrados">
          Registra la fecha histórica, el valor pagado y el comprobante desde la pestaña Pagos.
        </InfoPanel>
      )}
      <CanonicalModal
        open={open}
        size="medium"
        eyebrow="ALTA HISTÓRICA AUDITADA"
        title="Registrar ingreso y pago"
        description="El mes de ingreso será el primer período cobrable para esta alícuota."
        onClose={() => setOpen(false)}
      >
        <AdmissionForm
          items={items}
          memberId={memberId}
          onDone={(r) => {
            setItems(r);
            setOpen(false);
          }}
        />
      </CanonicalModal>
      <CanonicalModal
        open={Boolean(recalculation)}
        size="medium"
        eyebrow="RECÁLCULO AUDITADO"
        title={`Recalcular alícuota ${recalculation?.slotNumber ?? ''}`}
        description="Compara el estado actual con las obligaciones que corresponden a la fecha corregida."
        onClose={() => setRecalculation(null)}
      >
        {recalculationMessage ? <InfoPanel tone="danger">{recalculationMessage}</InfoPanel> : null}
        {recalculation ? (
          <div className="member-entry-payments__form">
            <InfoPanel title="Cambio de titularidad">
              Fecha anterior: {recalculation.oldFirstChargePeriod.slice(0, 7)} · Fecha corregida:{' '}
              {recalculation.newFirstChargePeriod.slice(0, 7)}
            </InfoPanel>
            <dl>
              <dt>Se conservan</dt>
              <dd>{recalculation.keepCount}</dd>
              <dt>Se anularán</dt>
              <dd>
                {recalculation.cancelCount} · USD {Number(recalculation.cancelAmount).toFixed(2)}
              </dd>
              <dt>Se crearán</dt>
              <dd>
                {recalculation.createCount} · USD {Number(recalculation.createAmount).toFixed(2)}
              </dd>
              <dt>Bloqueadas por pagos</dt>
              <dd>{recalculation.blockedCount}</dd>
            </dl>
            {recalculation.blockedCount ? (
              <InfoPanel tone="danger">
                Existen obligaciones con pagos. Deben resolverse mediante ajuste contable.
              </InfoPanel>
            ) : (
              <InfoPanel title="Protección financiera">
                No se borrará ningún registro; las obligaciones fuera del período quedarán anuladas
                y auditadas.
              </InfoPanel>
            )}
            <Button
              variant="primary"
              disabled={recalculationBusy || recalculation.blockedCount > 0}
              onClick={async () => {
                setRecalculationBusy(true);
                setRecalculationMessage('');
                try {
                  await confirmAdmissionRecalculation(
                    apiBase,
                    memberId,
                    recalculation.assignmentId,
                  );
                  setRecalculation(null);
                  load();
                } catch (error) {
                  setRecalculationMessage(
                    error instanceof Error ? error.message : 'No pudimos confirmar el recálculo.',
                  );
                } finally {
                  setRecalculationBusy(false);
                }
              }}
            >
              {recalculationBusy ? 'Recalculando…' : 'Confirmar recálculo'}
            </Button>
          </div>
        ) : null}
      </CanonicalModal>
    </div>
  );
}
function AdmissionForm({
  items,
  memberId,
  onDone,
}: {
  items: MemberAdmissionItem[];
  memberId: string;
  onDone: (items: MemberAdmissionItem[]) => void;
}) {
  const active = useMemo(() => items.filter((i) => !i.endedAt), [items]);
  const [assignmentId, setAssignmentId] = useState(''),
    [joinedOn, setJoinedOn] = useState(''),
    [amount, setAmount] = useState(''),
    [paidOn, setPaidOn] = useState(''),
    [reference, setReference] = useState(''),
    [method, setMethod] = useState(''),
    [notes, setNotes] = useState(''),
    [reason, setReason] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const ready = Boolean(
    assignmentId &&
    joinedOn &&
    amount &&
    Number(amount) > 0 &&
    paidOn &&
    reference.trim().length >= 3 &&
    method &&
    notes.trim().length >= 10 &&
    reason.trim().length >= 10 &&
    file?.type === 'application/pdf',
  );
  const save = async () => {
    if (!ready || !file) return;
    setBusy(true);
    setMessage('');
    try {
      const r = await recordMemberAdmissionPayment(apiBase, memberId, {
        assignmentId,
        joinedOn,
        amount,
        paidOn,
        reference,
        paymentMethod: method,
        notes,
        reason,
        file,
      });
      onDone(r.items);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo registrar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="member-entry-payments__form">
      {message ? <InfoPanel tone="danger">{message}</InfoPanel> : null}
      <SelectField
        label="Alícuota"
        required
        value={assignmentId}
        onValueChange={setAssignmentId}
        options={[
          { value: '', label: 'Selecciona una alícuota' },
          ...active.map((i) => ({ value: i.assignmentId, label: `Alícuota ${i.number}` })),
        ]}
      />
      <label>Fecha histórica de ingreso *</label>
      <input
        type="date"
        max="2024-10-24"
        value={joinedOn}
        onChange={(e) => setJoinedOn(e.target.value)}
      />
      <label>Primer período cobrable</label>
      <input
        readOnly
        value={joinedOn ? joinedOn.slice(0, 7) : 'Se calcula desde la fecha de ingreso'}
      />
      <label>Valor pagado (USD) *</label>
      <input
        type="number"
        min="0.01"
        step="0.01"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <label>Fecha de pago *</label>
      <input
        type="date"
        max="2024-10-24"
        value={paidOn}
        onChange={(e) => setPaidOn(e.target.value)}
      />
      <label>Referencia del comprobante *</label>
      <input value={reference} maxLength={200} onChange={(e) => setReference(e.target.value)} />
      <SelectField
        label="Forma de pago"
        required
        value={method}
        onValueChange={setMethod}
        options={[
          { value: '', label: 'Selecciona una forma' },
          { value: 'cash', label: 'Efectivo' },
          { value: 'bank_transfer', label: 'Transferencia bancaria' },
          { value: 'deposit', label: 'Depósito' },
          { value: 'check', label: 'Cheque' },
          { value: 'other', label: 'Otra' },
        ]}
      />
      <label>Observación del pago *</label>
      <textarea rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <label>Motivo y fuente de la fecha de ingreso *</label>
      <textarea
        rows={2}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <label>Comprobante PDF *</label>
      <input
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      <InfoPanel title="Control del cálculo">
        Guardar no genera obligaciones. La fecha se utilizará como límite inicial cuando se ejecute
        el proceso autorizado.
      </InfoPanel>
      <Button variant="primary" disabled={!ready || busy} onClick={() => void save()}>
        {busy ? 'Registrando…' : 'Registrar ingreso y pago'}
      </Button>
    </div>
  );
}
